// Synchronous product matcher for faster rendering
// This is a companion to productMatcher.js that provides synchronous lookup
// after canonical products are loaded

import Fuse from 'fuse.js';
import { loadCanonicalProducts } from './productMatcher';

// Internal state
let canonicalProducts = null;
let fuse = null;
let isInitialized = false;
let initializePromise = null;

// This loads the data and prepares the lookup, but only once
export async function initialize() {
  if (isInitialized) return;
  
  if (initializePromise) {
    return initializePromise;
  }
  
  initializePromise = (async () => {
    try {
      // Load canonical products
      canonicalProducts = await loadCanonicalProducts();
      
      // Create Fuse index
      fuse = new Fuse(canonicalProducts, {
        keys: ['skus', 'aliases', 'canonicalName'],
        threshold: 0.3, // Slightly more strict for better matching
        includeScore: true
      });
      
      isInitialized = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('Sync product matcher initialized with', canonicalProducts.length, 'products');
      }
      return true;
    } catch (error) {
      console.error('Failed to initialize sync product matcher:', error);
      // Reset so a future call can retry
      initializePromise = null;
      return false;
    }
  })();
  
  return initializePromise;
}

// Synchronous product lookup - will return null if not initialized
export function getCanonicalProductSync(productName) {
  if (!isInitialized || !fuse || !productName) {
    return null;
  }
  
  try {
    const result = fuse.search(productName);
    return result.length > 0 ? result[0].item : null;
  } catch (error) {
    console.warn('Error in sync product lookup:', error);
    return null;
  }
}

// Get all canonical products (synchronous)
export function getAllCanonicalProducts() {
  return canonicalProducts || [];
}

// Initialize on import
initialize().catch(console.error);

export default {
  initialize,
  getCanonicalProductSync,
  getAllCanonicalProducts
};
